import { useTranslation } from 'react-i18next';
import { LegalLayout, Section } from './LegalLayout';

interface Dep {
  name: string;
  what: string;
  license: 'MIT' | 'Apache-2.0' | 'ISC' | 'BSD-3-Clause' | 'BSD-2-Clause';
  url: string;
}

const BACKEND: Dep[] = [
  { name: 'NestJS', what: 'Backend framework — controllers, modules, DI', license: 'MIT', url: 'https://nestjs.com' },
  { name: 'TypeScript', what: 'Type system end-to-end', license: 'Apache-2.0', url: 'https://www.typescriptlang.org' },
  { name: 'node-postgres (pg)', what: 'Postgres driver', license: 'MIT', url: 'https://node-postgres.com' },
  { name: 'Qdrant client', what: 'Vector store client', license: 'Apache-2.0', url: 'https://github.com/qdrant/qdrant-js' },
  { name: 'OpenAI SDK', what: 'AI model gateway client (OpenAI-compatible shape)', license: 'Apache-2.0', url: 'https://github.com/openai/openai-node' },
  { name: 'jose', what: 'JWT + JWKS verification for OIDC', license: 'MIT', url: 'https://github.com/panva/jose' },
  { name: '@node-saml/node-saml', what: 'SAML 2.0 ACS handling', license: 'MIT', url: 'https://github.com/node-saml/node-saml' },
  { name: 'passport + strategies', what: 'OAuth (Google, Apple) + JWT cookie session', license: 'MIT', url: 'https://www.passportjs.org' },
  { name: 'bcryptjs', what: 'Password hashing', license: 'MIT', url: 'https://github.com/dcodeIO/bcrypt.js' },
  { name: 'class-validator + class-transformer', what: 'DTO validation', license: 'MIT', url: 'https://github.com/typestack/class-validator' },
  { name: 'BullMQ', what: 'Background job queue', license: 'MIT', url: 'https://docs.bullmq.io' },
  { name: 'Stripe SDK', what: 'Billing', license: 'MIT', url: 'https://stripe.com/docs/api' },
  { name: 'nodemailer', what: 'Transactional email', license: 'MIT', url: 'https://nodemailer.com' },
];

const FRONTEND: Dep[] = [
  { name: 'React', what: 'UI library', license: 'MIT', url: 'https://react.dev' },
  { name: 'Vite', what: 'Build + dev server', license: 'MIT', url: 'https://vitejs.dev' },
  { name: 'Tailwind CSS', what: 'Styling primitives', license: 'MIT', url: 'https://tailwindcss.com' },
  { name: 'React Router', what: 'Client-side routing', license: 'MIT', url: 'https://reactrouter.com' },
  { name: 'TanStack Query', what: 'Server-state cache', license: 'MIT', url: 'https://tanstack.com/query' },
  { name: 'Radix UI', what: 'Headless accessible primitives', license: 'MIT', url: 'https://www.radix-ui.com' },
  { name: 'i18next + react-i18next', what: 'Internationalization', license: 'MIT', url: 'https://www.i18next.com' },
  { name: 'framer-motion', what: 'UI animations', license: 'MIT', url: 'https://www.framer.com/motion' },
  { name: 'Apache ECharts', what: 'Charting', license: 'Apache-2.0', url: 'https://echarts.apache.org' },
  { name: 'Zustand', what: 'Lightweight client state', license: 'MIT', url: 'https://github.com/pmndrs/zustand' },
  { name: 'lucide-react', what: 'Icon set', license: 'ISC', url: 'https://lucide.dev' },
  { name: 'axios', what: 'HTTP client', license: 'MIT', url: 'https://axios-http.com' },
];

const INFRA: Dep[] = [
  { name: 'PostgreSQL', what: 'Primary data store', license: 'BSD-2-Clause', url: 'https://www.postgresql.org' },
  { name: 'Redis', what: 'Cache + queue backend', license: 'BSD-3-Clause', url: 'https://redis.io' },
  { name: 'Qdrant', what: 'Vector store (HNSW)', license: 'Apache-2.0', url: 'https://qdrant.tech' },
  { name: 'nginx', what: 'Edge reverse proxy + TLS termination', license: 'BSD-2-Clause', url: 'https://nginx.org' },
  { name: 'PM2', what: 'Node process manager in production', license: 'MIT', url: 'https://pm2.keymetrics.io' },
  { name: 'Tesseract (optional, on-prem)', what: 'Local OCR for air-gapped deployments', license: 'Apache-2.0', url: 'https://github.com/tesseract-ocr/tesseract' },
];

export function OssPage() {
  const { t } = useTranslation();
  return (
    <LegalLayout
      eyebrow={t('oss.eyebrow', { defaultValue: 'Open source' }) as string}
      title={t('oss.title', { defaultValue: "What Knowlez is built on." })}
      updated="2026-05-23"
    >
      <p className="text-[14.5px] text-ink-700 leading-relaxed">
        {t('oss.intro', {
          defaultValue:
            'Knowlez stands on a stack of open-source projects. We try to be honest about what we use and what we owe each project. The list below is current as of the latest release; the live dependency manifests in our public repository are the canonical source.',
        })}
      </p>

      <p className="mt-3 text-[14.5px] text-ink-700 leading-relaxed">
        {t('oss.publish', {
          defaultValue:
            'We also publish under MIT: the canonical chunk + entity + edge data-model spec, the Knowlez connector catalog schema, and the multilingual evaluation harness we use to benchmark our retrieval. Repository links live on github.com/knowlez.',
        })}
      </p>

      <Section id="backend" kicker="backend" title={t('oss.backend.title', { defaultValue: 'Backend' }) as string}>
        <DepTable rows={BACKEND} />
      </Section>

      <Section id="frontend" kicker="frontend" title={t('oss.frontend.title', { defaultValue: 'Frontend' }) as string}>
        <DepTable rows={FRONTEND} />
      </Section>

      <Section id="infra" kicker="infra" title={t('oss.infra.title', { defaultValue: 'Infrastructure' }) as string}>
        <DepTable rows={INFRA} />
      </Section>

      <Section id="font" kicker="typography" title={t('oss.fonts.title', { defaultValue: 'Typography' }) as string}>
        <p>
          {t('oss.fonts.body', {
            defaultValue:
              'Inter + Inter Tight (SIL Open Font License 1.1) by the Rasmus Andersson collective. JetBrains Mono (SIL OFL 1.1) by JetBrains. Noto Serif JP (SIL OFL 1.1) by the Google Noto project.',
          })}
        </p>
      </Section>

      <Section id="thanks" kicker="thanks" title={t('oss.thanks.title', { defaultValue: 'Thanks' }) as string}>
        <p>
          {t('oss.thanks.body', {
            defaultValue:
              'None of this exists without the people who maintain the projects above. If you maintain one of them, thank you — both for the work and for the patience that makes open-source possible.',
          })}
        </p>
      </Section>
    </LegalLayout>
  );
}

function DepTable({ rows }: { rows: Dep[] }) {
  return (
    <div className="overflow-x-auto -mx-2">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="text-left text-ink-400 border-b border-ink-100">
            <th className="py-2 px-2 font-medium text-[10px] uppercase tracking-[0.16em]">
              Package
            </th>
            <th className="py-2 px-2 font-medium text-[10px] uppercase tracking-[0.16em]">
              What
            </th>
            <th className="py-2 px-2 font-medium text-[10px] uppercase tracking-[0.16em]">
              License
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((d) => (
            <tr key={d.name} className="border-b border-ink-50 align-top">
              <td className="py-2 px-2">
                <a
                  href={d.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-ink-900 hover:text-accent-700 hover:underline"
                >
                  {d.name}
                </a>
              </td>
              <td className="py-2 px-2 text-ink-700">{d.what}</td>
              <td className="py-2 px-2 font-mono text-[11px] text-ink-500 num">{d.license}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
